import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API_BASE, fmtUSD, fmtPct } from "@/lib/api";
import { Save, Plus, X, ArrowRight, Sunrise, Calendar as CalendarIcon, Sparkles, MoonStar, Trash2, Video, Lock, Upload, Star } from "lucide-react";
import { toast } from "sonner";
import StreakBadge from "@/components/StreakBadge";
import VideoPlayer from "@/components/VideoPlayer";

const todayStr = () => new Date().toISOString().slice(0, 10);

const TYPE_LABELS = { text: "Open answer", scale: "1–5 scale", choice: "Multiple choice" };

const QuestionInput = ({ q, value, onChange }) => {
  if (q.type === "scale") {
    return (
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4, 5].map((n) => {
          const active = String(value) === String(n);
          return (
            <button
              key={n} type="button"
              onClick={() => onChange(String(n))}
              data-testid={`scale-${q.id}-${n}`}
              className={`w-12 h-12 border text-lg font-mono-num transition-all ${active
                ? "bg-[color:var(--accent)] text-black border-[color:var(--accent)] scale-110"
                : "bg-[color:var(--surface)] border-[color:var(--border)] text-[color:var(--fg-3)] hover:border-[color:var(--accent)]/40 hover:text-[color:var(--fg)]"}`}>
              {n}
            </button>
          );
        })}
        <span className="text-xs text-[color:var(--fg-3)] ml-2 font-mono-num">{value ? `→ ${value}/5` : "(unanswered)"}</span>
      </div>
    );
  }
  if (q.type === "choice") {
    return (
      <div className="flex flex-wrap gap-2">
        {q.options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt} type="button"
              onClick={() => onChange(opt)}
              data-testid={`choice-${q.id}-${opt}`}
              className={`px-4 py-2 border text-sm transition-all ${active
                ? "bg-[color:var(--accent)] text-black border-[color:var(--accent)]"
                : "bg-[color:var(--surface)] border-[color:var(--border)] text-[color:var(--fg-2)] hover:border-[color:var(--accent)]/40 hover:text-[color:var(--fg)]"}`}>
              {opt}
            </button>
          );
        })}
      </div>
    );
  }
  return (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={2}
      data-testid={`text-${q.id}`}
      className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--accent)] resize-none font-mono-num"
      placeholder="Type your reflection…"
    />
  );
};

export default function Recap() {
  const { date: paramDate } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [date, setDate] = useState(paramDate || todayStr());
  const [answers, setAnswers] = useState({});
  const [futureMsg, setFutureMsg] = useState("");
  const [addingQ, setAddingQ] = useState(false);
  const [newQ, setNewQ] = useState({ text: "", type: "text", options: "" });
  const [brief, setBrief] = useState("");
  const [briefStreaming, setBriefStreaming] = useState(false);
  const briefRef = useRef(null);

  /* eslint-disable */
  useEffect(() => { setDate(paramDate || todayStr()); }, [paramDate]);

  const { data: questions = [] } = useQuery({
    queryKey: ["recap-questions"],
    queryFn: async () => (await api.get("/recap/questions")).data,
  });

  const { data: recap } = useQuery({
    queryKey: ["recap", date],
    queryFn: async () => (await api.get(`/recap/${date}`)).data,
  });

  // Pro-gated Trade Replays for this recap day.
  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });
  const isPro = billing?.plan_tier === "pro";

  const { data: dayClips = [] } = useQuery({
    queryKey: ["video-clips", "day", date],
    queryFn: async () => (await api.get("/video/clips", { params: { date } })).data,
    enabled: isPro,
  });

  const toggleClipStar = useMutation({
    mutationFn: async ({ id, starred }) => (await api.patch(`/video/clips/${id}`, { starred })).data,
    onMutate: async ({ id, starred }) => {
      // Optimistic — flip the star immediately.
      await qc.cancelQueries({ queryKey: ["video-clips", "day", date] });
      const prev = qc.getQueryData(["video-clips", "day", date]);
      qc.setQueryData(["video-clips", "day", date], (old = []) =>
        old.map((c) => (c.id === id ? { ...c, starred } : c))
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["video-clips", "day", date], ctx.prev);
      toast.error("Couldn't update — try again");
    },
    onSuccess: (_d, v) => {
      toast.success(v.starred ? "Added to Weekly Reel" : "Removed from Weekly Reel");
      qc.invalidateQueries({ queryKey: ["weekly-reel-picks"] });
    },
  });

  useEffect(() => {
    if (recap?.entry) {
      setAnswers(recap.entry.answers || {});
      setFutureMsg(recap.entry.message_to_future_self || "");
      setBrief(recap.entry.edge_brief || "");
    }
  }, [recap, date]);
  /* eslint-enable */

  const streamBrief = async () => {
    setBrief("");
    setBriefStreaming(true);
    try {
      const token = localStorage.getItem("edge_token");
      const res = await fetch(`${API_BASE}/recap/${date}/brief`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setBrief((b) => b + chunk);
      }
      qc.invalidateQueries({ queryKey: ["recap", date] });
      qc.invalidateQueries({ queryKey: ["morning-message"] });
    } catch (e) {
      toast.error("Brief unavailable");
    } finally {
      setBriefStreaming(false);
      setTimeout(() => briefRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    }
  };

  const saveMut = useMutation({
    mutationFn: async () => (await api.put(`/recap/${date}`, {
      date,
      answers: Object.entries(answers).map(([qid, value]) => ({ question_id: qid, value })),
      message_to_future_self: futureMsg,
    })).data,
    onSuccess: () => {
      toast.success("RECAP SAVED · Generating edge brief…");
      qc.invalidateQueries({ queryKey: ["recap", date] });
      qc.invalidateQueries({ queryKey: ["morning-message"] });
      qc.invalidateQueries({ queryKey: ["recap-streak"] });
      qc.invalidateQueries({ queryKey: ["journal", date] });
      qc.invalidateQueries({ queryKey: ["journal-list"] });
      qc.invalidateQueries({ queryKey: ["calendar-day"] });
      streamBrief();
    },
    onError: () => toast.error("Failed to save"),
  });

  const addQuestionMut = useMutation({
    mutationFn: async () => (await api.post("/recap/questions", {
      text: newQ.text,
      type: newQ.type,
      options: newQ.type === "choice" ? newQ.options.split(",").map(s => s.trim()).filter(Boolean) : [],
      order: 200,
    })).data,
    onSuccess: () => {
      toast.success("QUESTION ADDED");
      setNewQ({ text: "", type: "text", options: "" });
      setAddingQ(false);
      qc.invalidateQueries({ queryKey: ["recap-questions"] });
    },
    onError: () => toast.error("Failed to add"),
  });

  const deleteQuestionMut = useMutation({
    mutationFn: async (id) => api.delete(`/recap/questions/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["recap-questions"] }); toast.success("REMOVED"); },
  });

  const summary = recap?.summary || { trades: 0, pnl: 0, wins: 0, losses: 0, win_rate: 0, symbols: [] };
  const pnlColor = summary.pnl >= 0 ? "text-[color:var(--green)]" : "text-[color:var(--loss)]";

  return (
    <div className="min-h-screen">
      {/* Hero header */}
      <div className="border-b border-[color:var(--border)] px-8 pt-10 pb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(168,85,247,0.08)] via-transparent to-transparent pointer-events-none" />
        <div className="relative flex items-end justify-between gap-6">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><MoonStar className="h-3 w-3" strokeWidth={1.5} /> DAILY DEBRIEF</div>
            <h1 className="font-heading text-5xl font-light mt-2 tracking-tight">
              Today&apos;s <span className="text-[color:var(--accent)]">recap</span>.
            </h1>
            <p className="text-sm text-[color:var(--fg-3)] mt-2 max-w-md">
              Close the day with intention. Honest answers compound faster than P&amp;L.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StreakBadge />
            <CalendarIcon className="h-4 w-4 text-[color:var(--fg-3)]" strokeWidth={1.5} />
            <input
              type="date" value={date}
              onChange={(e) => { setDate(e.target.value); navigate(`/recap/${e.target.value}`, { replace: true }); }}
              data-testid="recap-date-input"
              className="bg-[color:var(--surface)] border border-[color:var(--border)] px-3 py-2 text-sm font-mono-num focus:outline-none focus:border-[color:var(--accent)]"
            />
            <Link
              to="/import"
              data-testid="recap-import-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[color:var(--surface)] hover:bg-[color:var(--surface2)] border border-[color:var(--border)] text-[color:var(--fg-2)] transition-colors"
              title="Import a CSV if you haven't uploaded today's trades yet"
            >
              <Upload className="h-4 w-4" strokeWidth={1.5} /> IMPORT CSV
            </Link>
            <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
              data-testid="save-recap-btn"
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[color:var(--accent)] text-black hover:bg-[color:var(--accent)] transition-colors disabled:opacity-50">
              <Save className="h-4 w-4" strokeWidth={1.5} /> {saveMut.isPending ? "SAVING…" : "SAVE RECAP"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-8 py-10 space-y-10">
        {/* NO TRADE DAY callout — only when the user had zero trades. Encourages
            reflection even on days you sat out (a common discipline habit)
            instead of leaving the page feeling half-empty. Hidden as soon as
            any trades exist so it never distracts from real trading data. */}
        {summary.trades === 0 && (
          <div
            className="p-4 flex items-start gap-3"
            data-testid="no-trade-day-banner"
            style={{
              background: "rgba(168,85,247,0.06)",
              border: "1px dashed rgba(168,85,247,0.32)",
              borderRadius: "10px",
            }}
          >
            <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-md" style={{ background: "rgba(168,85,247,0.15)", color: "var(--accent)" }}>
              <CalendarIcon className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="label-eyebrow" style={{ color: "var(--accent)" }}>NO TRADE DAY</div>
              <div className="text-sm mt-1" style={{ color: "var(--fg-2)" }}>
                No trades logged today. Recap it anyway — reflecting on the days you sat out is how you avoid
                revenge-trading tomorrow. Note what you saw, what stopped you, and what you&apos;d change.
              </div>
            </div>
          </div>
        )}

        {/* Day Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[color:var(--border)] [&>*]:border-r [&>*]:border-[color:var(--border)] [&>*:last-child]:border-r-0">
          <div className="p-4"><div className="label-eyebrow">DAY P&L</div><div className={`font-mono-num text-2xl mt-1 ${pnlColor}`} data-testid="summary-pnl">{fmtUSD(summary.pnl)}</div></div>
          <div className="p-4"><div className="label-eyebrow">TRADES</div><div className="font-mono-num text-2xl mt-1 text-[color:var(--fg)]">{summary.trades}</div></div>
          <div className="p-4"><div className="label-eyebrow">WINS</div><div className="font-mono-num text-2xl mt-1 text-[color:var(--green)]">{summary.wins}</div></div>
          <div className="p-4"><div className="label-eyebrow">LOSSES</div><div className="font-mono-num text-2xl mt-1 text-rose-400">{summary.losses}</div></div>
          <div className="p-4"><div className="label-eyebrow">WIN RATE</div><div className="font-mono-num text-2xl mt-1 text-[color:var(--fg)]">{fmtPct(summary.win_rate)}</div></div>
        </div>

        {/* Trade Replays for this day — Pro feature. */}
        <div data-testid="recap-replays-section">
          {isPro ? (
            dayClips.length > 0 ? (
              <div className="p-5" style={{ background: "rgba(168,85,247,0.05)", border: "1px solid rgba(168,85,247,0.30)", borderRadius: "12px" }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="label-eyebrow flex items-center gap-1.5" style={{ color: "var(--accent)" }}>
                    <Video className="h-3 w-3" strokeWidth={1.5} /> {dayClips.length} TRADE {dayClips.length === 1 ? "REPLAY" : "REPLAYS"} · REWATCH BEFORE YOU REFLECT
                  </div>
                  <button
                    onClick={() => navigate("/replay")}
                    data-testid="recap-add-replays-btn"
                    className="flex items-center gap-1.5 text-[11px] text-[color:var(--accent)] hover:text-white transition-colors"
                  >
                    <Upload className="h-3 w-3" strokeWidth={1.5} /> ADD MORE
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[...dayClips]
                    // Chronological order — first trades of the day at the top
                    // so you can walk through your session in order. Falls back
                    // to created_at when entry_time is missing.
                    .sort((a, b) => {
                      const ta = new Date(a.entry_time || a.created_at || 0).getTime();
                      const tb = new Date(b.entry_time || b.created_at || 0).getTime();
                      return ta - tb;
                    })
                    .map((c) => (
                    <div key={c.id} className="text-xs" data-testid={`recap-clip-${c.id}`}>
                      <VideoPlayer
                        clip={c}
                        streamUrl={`${API_BASE}/video/clips/${c.id}/stream?token=${encodeURIComponent(localStorage.getItem("edge_token") || "")}`}
                      />
                      <div className="mt-1.5 flex items-center gap-2 text-[11px] font-mono-num">
                        <span style={{ color: "var(--fg)" }}>{c.symbol}</span>
                        <span style={{ color: c.pnl >= 0 ? "var(--green)" : "var(--loss)" }}>{fmtUSD(c.pnl)}</span>
                        <span style={{ color: "var(--fg-3)" }}>{new Date(c.entry_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                        <button
                          type="button"
                          onClick={() => toggleClipStar.mutate({ id: c.id, starred: !c.starred })}
                          disabled={toggleClipStar.isPending}
                          data-testid={`recap-clip-star-${c.id}`}
                          className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full transition-colors disabled:opacity-60"
                          style={{
                            background: c.starred ? "rgba(251,191,36,0.14)" : "transparent",
                            border: `1px solid ${c.starred ? "rgba(251,191,36,0.55)" : "var(--border)"}`,
                            color: c.starred ? "#fbbf24" : "var(--fg-3)",
                            fontSize: "10px",
                            letterSpacing: "0.05em",
                          }}
                          title={c.starred ? "Remove from Weekly Reel" : "Star to include in Weekly Reel"}
                        >
                          <Star className="h-3 w-3" strokeWidth={1.5} fill={c.starred ? "#fbbf24" : "none"} />
                          {c.starred ? "IN REEL" : "STAR FOR REEL"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/replay")}
                data-testid="recap-import-replays-btn"
                className="w-full flex items-center justify-between gap-4 p-5 text-left transition-colors group"
                style={{ background: "rgba(168,85,247,0.03)", border: "1px dashed rgba(168,85,247,0.30)", borderRadius: "12px" }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ background: "rgba(168,85,247,0.15)", color: "var(--accent)" }}>
                    <Upload className="h-4 w-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="label-eyebrow mb-1" style={{ color: "var(--accent)" }}>IMPORT TRADE REPLAYS</div>
                    <div className="text-xs" style={{ color: "var(--fg-2)" }}>
                      Drop your OBS recording. We auto-cut a clip per trade, tagged to {date}.
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={() => navigate("/pricing")}
              data-testid="recap-replays-upsell"
              className="w-full flex items-center justify-between gap-4 p-5 text-left transition-colors group"
              style={{ background: "rgba(168,85,247,0.03)", border: "1px dashed rgba(168,85,247,0.30)", borderRadius: "12px" }}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg" style={{ background: "rgba(168,85,247,0.15)", color: "var(--accent)" }}>
                  <Lock className="h-4 w-4" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="label-eyebrow mb-1" style={{ color: "var(--accent)" }}>TRADE REPLAYS · PRO</div>
                  <div className="text-xs" style={{ color: "var(--fg-2)" }}>
                    Rewatch the tape of every trade you took today. Upgrade to unlock Video Replay.
                  </div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            </button>
          )}
        </div>

        {/* Questions */}
        <div className="space-y-8">
          {questions.map((q, idx) => (
            <div key={q.id} className="group" data-testid={`question-${q.id}`}>
              <div className="flex items-baseline justify-between gap-4 mb-3">
                <div className="flex items-baseline gap-3">
                  <span className="label-eyebrow text-[color:var(--accent)]/60 font-mono-num">{String(idx + 1).padStart(2, "0")}</span>
                  <h3 className="font-heading text-lg text-[color:var(--fg)] font-light">{q.text}</h3>
                </div>
                {!q.is_default && (
                  <button onClick={() => deleteQuestionMut.mutate(q.id)} data-testid={`delete-q-${q.id}`}
                    className="opacity-0 group-hover:opacity-100 text-[color:var(--fg-3)] hover:text-rose-400 transition-all">
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </button>
                )}
              </div>
              <QuestionInput
                q={q}
                value={answers[q.id]}
                onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
              />
            </div>
          ))}

          {/* Add question */}
          <div className="border-t border-[color:var(--border)] pt-6">
            {!addingQ ? (
              <button onClick={() => setAddingQ(true)} data-testid="add-question-btn"
                className="flex items-center gap-2 text-xs text-[color:var(--fg-3)] hover:text-[color:var(--accent)] transition-colors">
                <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> ADD YOUR OWN QUESTION
              </button>
            ) : (
              <div className="border border-[color:var(--border)] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow">NEW QUESTION</div>
                  <button onClick={() => setAddingQ(false)} className="text-[color:var(--fg-3)] hover:text-[color:var(--fg)]"><X className="h-3.5 w-3.5" strokeWidth={1.5} /></button>
                </div>
                <input
                  type="text" value={newQ.text}
                  onChange={(e) => setNewQ({ ...newQ, text: e.target.value })}
                  placeholder="e.g. Did I respect my stop loss?"
                  data-testid="new-q-text"
                  className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--accent)]"
                />
                <div className="flex flex-wrap gap-2">
                  {["text", "scale", "choice"].map((t) => (
                    <button key={t} type="button" onClick={() => setNewQ({ ...newQ, type: t })}
                      data-testid={`new-q-type-${t}`}
                      className={`px-3 py-1.5 text-xs border transition-colors ${newQ.type === t ? "bg-[rgba(168,85,247,0.12)] text-[color:var(--accent)] border-[color:var(--accent)]" : "bg-[color:var(--surface)] text-[color:var(--fg-2)] border-[color:var(--border)] hover:border-[color:var(--accent)]/40"}`}>
                      {TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
                {newQ.type === "choice" && (
                  <input
                    type="text" value={newQ.options}
                    onChange={(e) => setNewQ({ ...newQ, options: e.target.value })}
                    placeholder="Comma-separated: Yes, No, Maybe"
                    data-testid="new-q-options"
                    className="w-full bg-[color:var(--surface)] border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--accent)] font-mono-num"
                  />
                )}
                <button onClick={() => addQuestionMut.mutate()} disabled={!newQ.text.trim() || addQuestionMut.isPending}
                  data-testid="submit-new-q"
                  className="bg-white text-black px-4 py-1.5 text-xs hover:bg-[color:var(--accent)] transition-colors disabled:opacity-40">
                  {addQuestionMut.isPending ? "ADDING…" : "ADD QUESTION"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Future Self Message */}
        <div className="border border-[color:var(--accent)]/30 bg-[rgba(168,85,247,0.05)] p-6 relative">
          <div className="absolute -top-3 left-6 bg-[color:var(--bg)] px-2 label-eyebrow text-[color:var(--accent)] flex items-center gap-1.5">
            <Sunrise className="h-3 w-3" strokeWidth={1.5} /> MESSAGE TO TOMORROW&apos;S YOU
          </div>
          <p className="text-xs text-[color:var(--fg-3)] mt-2 mb-3">
            The first thing you&apos;ll see when you log in tomorrow morning. Make it count.
          </p>
          <textarea
            value={futureMsg}
            onChange={(e) => setFutureMsg(e.target.value)}
            rows={4}
            data-testid="future-self-input"
            className="w-full bg-[color:var(--bg)] border border-[color:var(--border)] px-3 py-3 text-base focus:outline-none focus:border-[color:var(--accent)] resize-none font-mono-num leading-relaxed"
            placeholder="Dear tomorrow-me, here's what I want you to remember…"
          />
          <div className="flex items-center justify-between mt-4 text-xs text-[color:var(--fg-3)]">
            <span><Sparkles className="h-3 w-3 inline mr-1 text-[color:var(--accent)]" strokeWidth={1.5} /> A reminder. A warning. A pep talk. A rule. Whatever helps tomorrow-you.</span>
            <span className="font-mono-num">{futureMsg.length} chars</span>
          </div>
        </div>

        <button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}
          data-testid="bottom-save-btn"
          className="w-full bg-white text-black hover:bg-[color:var(--accent)] transition-colors py-4 text-sm font-medium tracking-wide flex items-center justify-center gap-2">
          {saveMut.isPending ? "SAVING…" : "SAVE RECAP & CLOSE THE DAY"}
          <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
        </button>

        {/* Edge Brief panel */}
        {(brief || briefStreaming) && (
          <div ref={briefRef} className="border-2 border-[color:var(--accent)]/40 bg-[rgba(168,85,247,0.05)] p-6 relative" data-testid="edge-brief-panel">
            <div className="absolute -top-3 left-6 bg-[color:var(--bg)] px-2 label-eyebrow text-[color:var(--accent)] flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" strokeWidth={1.5} /> TOMORROW&apos;S EDGE BRIEF · COACH
            </div>
            <p className="text-base text-[color:var(--fg)] mt-3 font-mono-num leading-relaxed whitespace-pre-wrap" data-testid="edge-brief-text">
              {brief}
              {briefStreaming && <span className="terminal-blink" />}
            </p>
            {!briefStreaming && brief && (
              <div className="mt-4 text-xs text-[color:var(--fg-3)] flex items-center gap-2">
                <Sparkles className="h-3 w-3 text-[color:var(--accent)]" strokeWidth={1.5} />
                Saved. This brief will greet you on the dashboard next morning.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
